import type { ReactElement } from 'react';
import { Alert, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import type { ProductWithMetadata } from 'api-client/models';
import { ResponsiveCardGrid } from '@/components/ResponsiveCardGrid';

interface Props {
  products: ProductWithMetadata[];
  pendingPrice: string | null;
  onChoose: (priceId: string) => void;
}

export default function BillingPlans({ products, pendingPrice, onChoose }: Props): ReactElement {
  if (!products.length)
    return <Alert severity="info">No subscription plans are available. Contact support for billing assistance.</Alert>;
  return (
    <Stack spacing={3}>
      <Typography>No active subscription. Choose an available plan to subscribe.</Typography>
      <ResponsiveCardGrid>
        {products.map(({ product, metadata, activePrices }) => (
          <Card key={product.id} component="article" aria-labelledby={`billing-plan-${product.id}`}>
            <CardContent>
              <Stack spacing={2}>
                <Typography id={`billing-plan-${product.id}`} component="h2" variant="h5">
                  {metadata.name || product.name}
                </Typography>
                {metadata.description && <Typography color="text.secondary">{metadata.description}</Typography>}
                {metadata.features.length > 0 && (
                  <Stack component="ul" spacing={1} sx={{ pl: 3, m: 0 }}>
                    {metadata.features.map((feature) => (
                      <Typography component="li" key={feature}>
                        {feature}
                      </Typography>
                    ))}
                  </Stack>
                )}
                {Object.entries(activePrices).map(
                  ([interval, price]) =>
                    price && (
                      <Stack key={price.id} spacing={1}>
                        <Typography>{price.paymentAmount}</Typography>
                        <Button
                          variant="contained"
                          disabled={pendingPrice !== null}
                          loading={pendingPrice === price.id}
                          onClick={() => onChoose(price.id)}
                        >
                          Choose {interval === 'month' ? 'monthly' : interval === 'year' ? 'annual' : interval}
                        </Button>
                      </Stack>
                    ),
                )}
                {!Object.values(activePrices).some(Boolean) && (
                  <Typography color="text.secondary">No prices are available for this plan.</Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </ResponsiveCardGrid>
    </Stack>
  );
}
